import { useCallback, useEffect, useRef, useState } from "react";

import { createPartnerPayout, deletePartnerPayout, getPartnerPayouts } from "services/partners/rest";
import { PartnerPayoutDto, PartnerPayoutsDto } from "services/partners/restModels";
import { CreatePartnerPayoutRequestDto } from "services/partners/restRequests";
import { UseNotificationSnackbarReturn } from "shared/ui/NotificationSnackbar/useNotificationSnackbar";

export const usePartnerPayouts = (
    fromTime: number | null,
    toTime: number | null,
    partnerId: number | null,
    notify: UseNotificationSnackbarReturn
) => {
    const { showError, showSuccess } = notify;

    const [payouts, setPayouts] = useState<PartnerPayoutsDto | null>(null);
    const [saving, setSaving] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [deleting, setDeleting] = useState<PartnerPayoutDto | null>(null);

    const requestId = useRef(0);

    const loadPayouts = useCallback(async () => {
        if (fromTime === null || toTime === null) return;

        const currentRequest = requestId.current + 1;
        requestId.current = currentRequest;

        const response = await getPartnerPayouts({ from: fromTime, to: toTime, partnerId }).catch(() => null);

        if (currentRequest !== requestId.current) return;

        if (response) {
            setPayouts(response);
        } else {
            showError("Не удалось загрузить выплаты");
        }
    }, [fromTime, toTime, partnerId, showError]);

    useEffect(() => {
        loadPayouts();
    }, [loadPayouts]);

    const run = async (action: () => Promise<unknown>, successTitle: string, failureTitle: string) => {
        setSaving(true);

        try {
            await action();
            showSuccess(successTitle);
            setIsCreating(false);
            setDeleting(null);
            await loadPayouts();
        } catch (caught) {
            showError(failureTitle, caught);
        } finally {
            setSaving(false);
        }
    };

    return {
        payouts,
        saving,
        isCreating,
        deleting,
        onStartCreating: () => setIsCreating(true),
        onCancelCreating: () => setIsCreating(false),
        onCreate: (body: CreatePartnerPayoutRequestDto) =>
            run(() => createPartnerPayout(body), "Выплата зафиксирована", "Не удалось сохранить выплату"),
        onStartDeleting: setDeleting,
        onCancelDeleting: () => setDeleting(null),
        onConfirmDeleting: () => {
            if (deleting) {
                run(() => deletePartnerPayout(deleting.id), "Выплата удалена", "Не удалось удалить выплату");
            }
        },
    };
};
